// Computes a scan's point-cloud footprint (for the anomaly-management minimap) from its LOCAL vertex
// positions — the model version's per-scan transform is applied later at render. For each of the two
// views (plot = top-down X/Z, elevation = front X/Y) it returns a `fill`: a downsampled mid-band slice
// of points (kept as full [x,y,z] so a 3D transform can be applied). The minimap rasterises these into
// an occupancy grid, so the true concave shape shows — no fragile hull. Cheap enough to run on idle.

const SAMPLE_TARGET = 60000;   // downsample the cloud to ~this many points for the slice work
const PLOT_FILL_CAP = 800;     // plan footprint points (the tile we actually draw — keep it dense)
const ELEV_FILL_CAP = 200;     // elevation slice points (only used to derive the height range now)
const BAND_FRAC = 0.10;        // slice band = ±10% of the height (plot) / depth (elevation)
const SIG_FIGS = 5;            // relative precision — robust across tiny and huge coordinate scales
// Azure Table Storage caps a string property at ~32K UTF-16 chars (64 KB). Stay well under it, and
// (below) trim the arrays until the serialised JSON fits, so a pathological cloud can't 500.
const MAX_JSON_CHARS = 28000;

const round = (v) => Number(v.toPrecision(SIG_FIGS));
const roundPt = (p) => [round(p[0]), round(p[1]), round(p[2])];

// Even-stride downsample of an array to at most `cap` items.
function downsample(arr, cap) {
  if (arr.length <= cap) return arr;
  const stride = arr.length / cap;
  const out = [];
  for (let i = 0; i < cap; i++) out.push(arr[Math.floor(i * stride)]);
  return out;
}

// Find the densest layer along `axis` (0=x,1=y,2=z), i.e. where the most structure is — a far more
// informative plan cut than a blind mid-height. The bottom ~15% and top ~10% are excluded so the
// slab/floor and roof (usually the densest bands) don't win and give a solid rectangle.
function densestLevel(pts, axis, lo, hi) {
  const range = hi - lo;
  if (range <= 0) return (lo + hi) / 2;
  const BINS = 50;
  const counts = new Array(BINS).fill(0);
  for (const p of pts) {
    let b = Math.floor(((p[axis] - lo) / range) * BINS);
    if (b < 0) b = 0;
    if (b >= BINS) b = BINS - 1;
    counts[b]++;
  }
  const loBin = Math.floor(BINS * 0.15);
  const hiBin = Math.ceil(BINS * 0.90);
  let best = loBin, bestC = -1;
  for (let b = loBin; b < hiBin; b++) {
    if (counts[b] > bestC) { bestC = counts[b]; best = b; }
  }
  return lo + ((best + 0.5) / BINS) * range;
}

/**
 * @param {ArrayLike<number>} positions flat [x,y,z,x,y,z,…] Float32Array of scan-local vertex coords
 * @param {number} count number of vertices (positions.length / 3)
 * @returns {{plot:{fill:number[][]}, elevation:{fill:number[][]}}|null}
 */
export function computeScanPerimeter(positions, count) {
  if (!positions || !count || count < 3) return null;

  const stride = Math.max(1, Math.floor(count / SAMPLE_TARGET));
  const pts = [];
  let minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < count; i += stride) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    pts.push([x, y, z]);
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  if (pts.length < 3) return null;

  // Up axis is Z (top-down plan = X/Y; height = Z). Plot: a horizontal cut at the DENSEST height
  // (equipment level) along Z. Elevation: a mid-depth (Y) cut — used to derive the height (Z) range.
  const sliceZ = densestLevel(pts, 2, minZ, maxZ);
  const bandZ = Math.max((maxZ - minZ) * BAND_FRAC, 1e-9);
  const midY = (minY + maxY) / 2, bandY = Math.max((maxY - minY) * BAND_FRAC, 1e-9);

  const result = {
    plot: { fill: downsample(pts.filter(p => Math.abs(p[2] - sliceZ) <= bandZ), PLOT_FILL_CAP).map(roundPt) },
    elevation: { fill: downsample(pts.filter(p => Math.abs(p[1] - midY) <= bandY), ELEV_FILL_CAP).map(roundPt) },
  };

  // Hard guarantee the payload fits Table Storage: thin the slices until the JSON is under budget.
  return fitWithinBudget(result);
}

function fitWithinBudget(result) {
  const size = () => JSON.stringify(result).length;
  for (let guard = 0; guard < 40 && size() > MAX_JSON_CHARS; guard++) {
    result.plot.fill = downsample(result.plot.fill, Math.max(20, Math.floor(result.plot.fill.length * 0.7)));
    result.elevation.fill = downsample(result.elevation.fill, Math.max(20, Math.floor(result.elevation.fill.length * 0.7)));
  }
  return result;
}
